import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Database, 
  Trash2, 
  Download, 
  Upload,
  Filter,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Users,
  FileText,
  Settings,
  Eye,
  Edit,
  X
} from 'lucide-react';

const EnhancedDatabaseAdmin = ({ onClose, adminToken, onDataChanged }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [operationResult, setOperationResult] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const token = adminToken || localStorage.getItem('adminToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Load all data
      const [membersRes, appsRes, contractsRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/members/search`, { headers }),
        fetch(`${backendUrl}/api/admin/applications/search`, { headers }),
        fetch(`${backendUrl}/api/admin/contracts`, { headers })
      ]);

      if (membersRes.ok) {
        const membersData = await membersRes.json();
        // The search endpoints return arrays directly, not wrapped in objects
        setMembers(Array.isArray(membersData) ? membersData : membersData.members || []);
      }

      if (appsRes.ok) {
        const appsData = await appsRes.json();
        // The search endpoints return arrays directly, not wrapped in objects  
        setApplications(Array.isArray(appsData) ? appsData : appsData.applications || []);
      }

      if (contractsRes.ok) {
        const contractsData = await contractsRes.json();
        setContracts(contractsData.contracts || []);
      }

      // Calculate stats
      calculateStats();

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setOperationResult({ type: 'error', message: 'Failed to load dashboard data' });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const totalMembers = members.length;
    const totalApplications = applications.length;
    const basicMembers = members.filter(m => m.membership_level === 'Basic').length;
    const standardMembers = members.filter(m => m.membership_level === 'Standard').length;
    const premiumMembers = members.filter(m => m.membership_level === 'Premium').length;
    const pendingApps = applications.filter(a => a.status === 'pending').length;

    setStats({
      totalMembers,
      totalApplications,
      basicMembers,
      standardMembers,
      premiumMembers,
      pendingApps,
      totalContracts: contracts.length
    });
  };

  const identifyTestData = (email, name) => {
    const emailLower = (email || '').toLowerCase();
    const nameLower = (name || '').toLowerCase();
    
    // Comprehensive test email patterns
    const testEmailPatterns = [
      'test@', 'example@', 'demo@', 'sample@', 'debug@',
      '@test.com', '@example.com', '@demo.com', '@sample.com',
      '.test@', 'test.', 'demo.', 'sample.', 'debug.',
      'contract.test', 'basic.member.', 'premium.user@test',
      'john.smith.test', 'debug.error.test', 'debug.valid.test',
      'testfix', 'validation@example', 'contract.test'
    ];
    
    // Test name patterns  
    const testNames = [
      'test user', 'demo user', 'sample user', 'test member',
      'debug user', 'contract test', 'basic test', 'premium test',
      'john smith test', 'validation test'
    ];
    
    // Check for real email providers to avoid false positives
    const realProviders = ['@gmail.com', '@yahoo.com', '@hotmail.com', '@outlook.com', '@icloud.com', '@aol.com'];
    const isRealProvider = realProviders.some(provider => emailLower.includes(provider));
    
    // If it's a real email provider, don't mark as test data unless it has obvious test patterns
    if (isRealProvider) {
      const obviousTestPatterns = ['test123', 'demo123', 'sample123'];
      return obviousTestPatterns.some(pattern => emailLower.includes(pattern));
    }
    
    // For other emails, check comprehensive test patterns
    const hasTestEmail = testEmailPatterns.some(pattern => emailLower.includes(pattern));
    const hasTestName = testNames.some(pattern => nameLower.includes(pattern));
    
    return hasTestEmail || hasTestName;
  };

  const getTestDataStats = () => {
    const testMembers = members.filter(m => 
      identifyTestData(m.email || '', `${m.first_name} ${m.last_name}`)
    );
    const testApplications = applications.filter(a => 
      identifyTestData(a.email || '', `${a.first_name} ${a.last_name}`)
    );

    return {
      testMembersCount: testMembers.length,
      testApplicationsCount: testApplications.length,
      realMembersCount: members.length - testMembers.length,
      realApplicationsCount: applications.length - testApplications.length
    };
  };

  const cleanupTestData = async () => {
    setLoading(true);
    try {
      const token = adminToken || localStorage.getItem('adminToken');
      
      const response = await fetch(`${backendUrl}/api/admin/cleanup-test-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok) {
        setOperationResult({
          type: 'success',
          message: `Test data cleaned successfully! Removed ${result.deleted_members} test members and ${result.deleted_applications} test applications.`
        });
        await loadDashboardData(); // Refresh Enhanced Database Admin data
        
        // Notify main dashboard to refresh its data
        if (onDataChanged) {
          await onDataChanged();
        }
      } else {
        throw new Error(result.detail || 'Cleanup failed');
      }
    } catch (error) {
      setOperationResult({
        type: 'error',
        message: `Cleanup failed: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const token = adminToken || localStorage.getItem('adminToken');
      
      const response = await fetch(`${backendUrl}/api/admin/export-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lpa-database-backup-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        setOperationResult({
          type: 'success',
          message: 'Database backup exported successfully!'
        });
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      setOperationResult({
        type: 'error',
        message: `Export failed: ${error.message}`
      });
    }
  };

  const deleteSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setLoading(true);
    try {
      const token = adminToken || localStorage.getItem('adminToken');
      
      const response = await fetch(`${backendUrl}/api/admin/delete-items`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: selectedItems,
          type: activeTab
        })
      });

      const result = await response.json();

      if (response.ok) {
        setOperationResult({
          type: 'success',
          message: `Successfully deleted ${selectedItems.length} items.`
        });
        setSelectedItems([]);
        setShowDeleteConfirm(false);
        await loadDashboardData();
        
        // Notify main dashboard to refresh its data
        if (onDataChanged) {
          await onDataChanged();
        }
      } else {
        throw new Error(result.detail || 'Delete failed');
      }
    } catch (error) {
      setOperationResult({
        type: 'error',
        message: `Delete failed: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = () => {
    let data = [];
    if (activeTab === 'members') data = members;
    else if (activeTab === 'applications') data = applications;
    else if (activeTab === 'contracts') data = contracts;

    if (searchTerm) {
      data = data.filter(item => 
        (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.first_name && item.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.last_name && item.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filterType !== 'all') {
      if (filterType === 'test') {
        data = data.filter(item => 
          identifyTestData(item.email || '', `${item.first_name} ${item.last_name}`)
        );
      } else if (filterType === 'real') {
        data = data.filter(item => 
          !identifyTestData(item.email || '', `${item.first_name} ${item.last_name}`)
        );
      }
    }

    return data;
  };

  const testStats = getTestDataStats();

  return (
    <div className="space-y-6 p-6">
          {/* Operation Result */}
          {operationResult && (
            <div className={`p-4 rounded-lg border ${
              operationResult.type === 'success' 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center space-x-2">
                {operationResult.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                )}
                <span className={`text-sm font-medium ${
                  operationResult.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {operationResult.message}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setOperationResult(null)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            {[
              { id: 'overview', label: 'Overview', icon: Settings },
              { id: 'members', label: 'Members', icon: Users },
              { id: 'applications', label: 'Applications', icon: FileText },
              { id: 'cleanup', label: 'Data Cleanup', icon: Trash2 }
            ].map(tab => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center space-x-2"
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </Button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.totalMembers}</div>
                    <div className="text-sm text-gray-600">Total Members</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{stats.totalApplications}</div>
                    <div className="text-sm text-gray-600">Applications</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{stats.totalContracts}</div>
                    <div className="text-sm text-gray-600">Signed Contracts</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{stats.pendingApps}</div>
                    <div className="text-sm text-gray-600">Pending Apps</div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Membership Distribution</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>Basic Members</span>
                      <Badge variant="secondary">{stats.basicMembers}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Standard Members</span>
                      <Badge className="bg-amber-100 text-amber-800">{stats.standardMembers}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Premium Members</span>
                      <Badge className="bg-blue-100 text-blue-800">{stats.premiumMembers}</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Data Quality</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>Real Members</span>
                      <Badge className="bg-green-100 text-green-800">{testStats.realMembersCount}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Test Members</span>
                      <Badge className="bg-red-100 text-red-800">{testStats.testMembersCount}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Real Applications</span>
                      <Badge className="bg-green-100 text-green-800">{testStats.realApplicationsCount}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Test Applications</span>
                      <Badge className="bg-red-100 text-red-800">{testStats.testApplicationsCount}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button onClick={loadDashboardData} disabled={loading}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Data
                </Button>
                <Button onClick={exportData} variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export Backup
                </Button>
              </div>
            </div>
          )}

          {/* Data Cleanup Tab */}
          {activeTab === 'cleanup' && (
            <div className="space-y-6">
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="text-orange-800">Test Data Cleanup</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-orange-800 mb-2">Test Data Found:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• {testStats.testMembersCount} test member accounts</li>
                        <li>• {testStats.testApplicationsCount} test applications</li>
                        <li>• Patterns: test@, example@, demo@, sample@</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-green-800 mb-2">Real Data (Will Be Preserved):</h4>
                      <ul className="text-sm space-y-1">
                        <li>• {testStats.realMembersCount} real member accounts</li>
                        <li>• {testStats.realApplicationsCount} real applications</li>
                        <li>• All legitimate email domains preserved</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <Button 
                      onClick={cleanupTestData} 
                      disabled={loading || testStats.testMembersCount === 0}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clean Up Test Data
                    </Button>
                    <p className="text-xs text-orange-600 mt-2">
                      This will permanently delete all identified test data while preserving real user information.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Members/Applications Tabs */}
          {(activeTab === 'members' || activeTab === 'applications') && (
            <div className="space-y-4">
              {/* Search and Filter */}
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center space-x-2">
                  <Search className="w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="all">All Data</option>
                  <option value="real">Real Users Only</option>
                  <option value="test">Test Data Only</option>
                </select>

                <Button onClick={loadDashboardData} size="sm" variant="outline">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {/* Data Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-3 text-left">Name</th>
                        <th className="p-3 text-left">Email</th>
                        <th className="p-3 text-left">Level</th>
                        <th className="p-3 text-left">Status</th>
                        <th className="p-3 text-left">Type</th>
                        <th className="p-3 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData().map((item, index) => {
                        const isTest = identifyTestData(item.email || '', `${item.first_name} ${item.last_name}`);
                        return (
                          <tr key={item.id || index} className="border-t hover:bg-gray-50">
                            <td className="p-3">
                              {item.first_name} {item.last_name}
                            </td>
                            <td className="p-3">{item.email}</td>
                            <td className="p-3">
                              <Badge className={
                                item.membership_level === 'Premium' ? 'bg-blue-100 text-blue-800' :
                                item.membership_level === 'Standard' ? 'bg-amber-100 text-amber-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {item.membership_level}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge variant={item.status === 'active' ? 'default' : 'secondary'}>
                                {item.status}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge className={isTest ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                                {isTest ? 'Test' : 'Real'}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center space-x-2">
                                <Button size="sm" variant="ghost">
                                  <Eye className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost">
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
    </div>
  );
};

export default EnhancedDatabaseAdmin;